import { t } from "@lingui/macro";
import { functions } from "@repo/ui";
import { httpsCallable } from "firebase/functions";
import { ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { Button } from "./ui/button";

interface PendingRequest {
	requestId: string;
	agentId: string;
	stagingVersion: number;
	stagingMergeSha: string;
	credentialId: string;
	clientId: string | null;
	createdAt: string;
	expiresAt: string;
}

export function ExternalAgentDeploymentApprovalsCard({
	companyId,
}: {
	companyId: string;
}) {
	const [requests, setRequests] = useState<PendingRequest[]>([]);
	const [loading, setLoading] = useState(false);
	const [deciding, setDeciding] = useState<string | null>(null);
	const load = useCallback(async () => {
		setLoading(true);
		try {
			const call = httpsCallable<
				{ companyId: string },
				{ requests: PendingRequest[] }
			>(functions, "listExternalAgentDeploymentApprovals");
			setRequests((await call({ companyId })).data.requests);
		} catch {
			toast.error(
				t({
					id: "deploymentApprovalsLoadFailed",
					message: "Unable to load deployment approvals.",
				}),
			);
		} finally {
			setLoading(false);
		}
	}, [companyId]);
	useEffect(() => {
		void load();
	}, [load]);
	const decide = async (item: PendingRequest, decision: "approve" | "deny") => {
		setDeciding(item.requestId);
		try {
			const call = httpsCallable(functions, "decideExternalAgentDeployment");
			await call({
				companyId,
				agentId: item.agentId,
				requestId: item.requestId,
				decision,
			});
			setRequests((current) =>
				current.filter((request) => request.requestId !== item.requestId),
			);
			toast.success(
				decision === "approve"
					? t({
							id: "deploymentApproved",
							message: "Live deployment approved and queued.",
						})
					: t({ id: "deploymentDenied", message: "Live deployment denied." }),
			);
		} catch (caught) {
			toast.error(
				caught instanceof Error
					? caught.message
					: t({
							id: "deploymentDecisionFailed",
							message: "Unable to decide this deployment request.",
						}),
			);
		} finally {
			setDeciding(null);
		}
	};
	return (
		<CollapsibleSettingsPanel
			icon={<ShieldCheck className="h-5 w-5" />}
			title={t({
				id: "deploymentApprovalsTitle",
				message: "Live deployment approvals",
			})}
			description={t({
				id: "deploymentApprovalsDescription",
				message:
					"Review Live deployments requested through the Developer API or MCP.",
			})}
		>
			<div className="space-y-3">
				<Button
					variant="outline"
					onClick={() => void load()}
					disabled={loading}
				>
					{t({ id: "deploymentApprovalsRefresh", message: "Refresh requests" })}
				</Button>
				{!loading && requests.length === 0 && (
					<p className="text-sm text-muted-foreground">
						{t({
							id: "deploymentApprovalsEmpty",
							message: "No Live deployments are awaiting approval.",
						})}
					</p>
				)}
				{requests.map((item) => (
					<div key={item.requestId} className="space-y-2 rounded-md border p-4">
						<p className="font-medium">
							{t({ id: "deploymentApprovalAgent", message: "Agent" })}:{" "}
							<bdi>{item.agentId}</bdi> ·{" "}
							{t({
								id: "deploymentApprovalTestingVersion",
								message: "Testing version",
							})}{" "}
							{item.stagingVersion}
						</p>
						<p className="text-sm text-muted-foreground">
							{t({
								id: "deploymentApprovalRequestedBy",
								message: "Requested by API key",
							})}{" "}
							<bdi>{item.credentialId}</bdi>
							{item.clientId ? ` (${item.clientId})` : ""}
						</p>
						<p className="text-sm text-muted-foreground">
							{t({ id: "deploymentApprovalRequestId", message: "Request ID" })}:{" "}
							<bdi>{item.requestId}</bdi>
						</p>
						<p className="text-sm text-muted-foreground">
							{t({ id: "deploymentApprovalRequestedAt", message: "Requested" })}
							: <bdi>{new Date(item.createdAt).toLocaleString()}</bdi>
						</p>
						<p className="text-sm text-muted-foreground">
							{t({ id: "deploymentApprovalExpires", message: "Expires" })}:{" "}
							<bdi>{new Date(item.expiresAt).toLocaleString()}</bdi>
						</p>
						<div className="flex gap-2">
							<Button
								disabled={
									deciding !== null || Date.parse(item.expiresAt) <= Date.now()
								}
								onClick={() => void decide(item, "approve")}
							>
								{t({
									id: "deploymentApprovalApprove",
									message: "Approve Live deployment",
								})}
							</Button>
							<Button
								variant="outline"
								disabled={deciding !== null}
								onClick={() => void decide(item, "deny")}
							>
								{t({ id: "deploymentApprovalDeny", message: "Deny" })}
							</Button>
						</div>
					</div>
				))}
			</div>
		</CollapsibleSettingsPanel>
	);
}
